export type Mode =
  | "email" | "phone" | "in_person" | "at_a_screen" | "out_of_the_house" | "paperwork";

export const MODE_LABELS: Record<Mode, string> = {
  email: "email",
  phone: "phone",
  in_person: "in person",
  at_a_screen: "at a screen",
  out_of_the_house: "out of the house",
  paperwork: "paperwork",
};

export interface Task {
  id: string;
  area_id: string | null; area_name: string | null;
  value_id: string | null; value_name: string | null; value_colour: string | null;
  title: string;
  tier: "must" | "should" | "nice" | null;
  why: string | null; next: string | null;
  state: "open" | "blocked" | "waiting" | "done" | "parked";
  due: string | null; blocked_until: string | null;
  mins: number | null; money: string | null; note: string | null;
  parked_because: string | null; review_when: string | null;
  sort: number;
  blocked: boolean; blocked_by: string[]; unblocks: string[];
  why_chain: string[]; breadcrumb: string;
  follow_up_on: string | null;
  needs_business_hours: boolean;
  mode: Mode | null;
  /**
   * What to show as the next action. The API has already applied the two
   * presentation rules — draft the script or the email first, chase rather
   * than wait — so nothing here needs to know about them. `mins` arrives
   * already null on a phone task.
   */
  next_now: string | null;
  following_up: boolean;
  draft_first: boolean;
}

export interface Routine {
  id: string; slot: "morning" | "afternoon" | "evening"; name: string; sort: number;
  /** Read-only prose shown when the line is tapped — the ordered fallbacks behind an eating line. Not state. */
  detail: string | null;
}
export interface Routines { morning: Routine[]; afternoon: Routine[]; evening: Routine[] }

export interface Travel {
  id: string; starts_on: string; ends_on: string; label: string; note: string | null;
}

export interface MoneyLine {
  id: string; section: "this_month" | "surprises" | "owed";
  label: string; amount: string | null; worst_case: string | null; note: string | null;
  task_id: string | null; task_title: string | null; task_state: string | null; task_due: string | null;
}

export interface MoneyPanel {
  date: string;
  this_month: MoneyLine[];
  next_90_days: {
    from: string; to: string;
    dated: { id: string; title: string; money: string; due: string; state: string; value_colour: string | null; area_name: string | null }[];
    beyond: { id: string; title: string; money: string; due: string }[];
    undated: { id: string; title: string; money: string }[];
  };
  surprises: MoneyLine[];
  owed: MoneyLine[];
}

export interface CalendarEvent {
  id: number; source: string; summary: string | null; location: string | null;
  all_day: boolean; busy: boolean; status: string | null;
  starts_at: string; ends_at: string;
  on_day: string; end_day: string; from_time: string; to_time: string;
}

export interface CalendarSource {
  id: string; label: string; colour: string; configured: boolean;
  first_polled_at: string | null;
  last_poll: { ok: boolean; status: number | null; events: number; fresh: number; ms: number; error: string | null; ran_at: string } | null;
}

export interface CalendarView {
  from: string; to: string; sources: CalendarSource[]; events: CalendarEvent[];
}

export interface Staleness {
  poll_every_minutes: number;
  by_source: { source: string; samples: number; fastest_mins: number; median_ish_mins: number; slowest_mins: number }[];
  note: string | null;
  recent_polls: { source: string; ok: boolean; status: number | null; events: number; fresh: number; ms: number; error: string | null; ran_at: string }[];
}

export interface Flag {
  id: string; title: string; body: string | null;
  state: "open" | "resolved"; resolution: string | null; tasks: string[];
}

export interface Rhythm { id: string; name: string; value_name: string; value_colour: string; note: string | null }

/**
 * A slot, as the API hands it over: its name and the one item it is offering.
 *
 * There is no `rest_days` here and no `last_advanced_on`, because the API does
 * not send them. That is deliberate and it is the guard — a client that never
 * receives the numbers cannot render "2 days to go", a last-done date, or a
 * count, however reasonable one of those looks later. A slot inside its rest
 * interval, or with nothing in it yet, does not arrive at all.
 */
export interface SlotItem { id: string; label: string; detail: string | null }
export interface Slot {
  id: string; name: string;
  value_id: string; value_name: string | null; value_colour: string | null;
  time_of_day: "morning" | "afternoon" | "evening" | "any";
  note: string | null;
  item: SlotItem;
}

/**
 * A shelf-stable staple, and whether it is in the flat. The unticked ones are
 * the shopping list.
 *
 * There is no `last_restocked` here and no count of what is missing, because
 * the API sends neither and the table holds neither. That is the guard, the
 * same one the slots have: a client that never receives a number cannot render
 * it, and neither can a later session reading only this file.
 */
export interface PantryItem { id: string; name: string; section: string; in_stock: boolean; sort: number }
export interface Pantry { sections: { name: string; items: PantryItem[] }[] }

export interface CollectionItem { id: string; text: string; created_at: string; promoted_task_id: string | null }
export interface Collection {
  id: string; name: string; value_id: string; value_name: string; value_colour: string;
  why: string | null; items: CollectionItem[];
}

/** One markdown file in the docs layer. The cockpit only ever reads these. */
export interface Doc { path: string; size: number; modified: string }

/**
 * The assistant. Exactly three models and two efforts, both decided by the
 * server — the list is fetched rather than written here so a fourth cannot
 * appear by editing the app.
 */
export interface AssistantModel { id: string; label: string; note: string }
export interface AssistantOptions {
  models: AssistantModel[]; efforts: string[];
  default_model: string; default_effort: string;
}
export interface AssistantSpend {
  input_tokens: number; cache_write_tokens: number;
  cache_read_tokens: number; output_tokens: number; usd: number;
}
/**
 * One field of a proposed change, before and after. `from` is null on
 * something being created.
 */
export interface AssistantLine { field: string; from: string | null; to: string | null }

/**
 * A change the assistant wants to make, rendered before it happens.
 *
 * Nothing behind this has run. The server holds the proposal and will only act
 * on it when `assistantConfirm` is called with the id — so what runs is what
 * was on this card and cannot be anything else.
 */
export interface AssistantPreview {
  tool: string;
  headline: string;
  lines: AssistantLine[];
  /** False only for a log line, which nothing in this system can take back. */
  undoable: boolean;
  caution: string | null;
}

export interface AssistantReply {
  reply: string;
  /** The conversation in the API's own shape. Opaque — echoed back next turn. */
  messages: unknown[];
  model: string; effort: string; spend: AssistantSpend; stopped: boolean;
  /** Present when the turn stopped at a change and is waiting for an answer. */
  pending?: { proposal_id: string; previews: AssistantPreview[] };
  /** Present on the turn after a confirm. `id` is the handle Undo takes. */
  applied?: { id: string; did: string[]; undoable: boolean };
}
/** Money, and only money. There is no call count here and there must not be. */
export interface AssistantCost {
  month: string; currency: string; total: number;
  by_model: { model: string; usd: number }[];
}

export interface Settings {
  id: number; model: string; dive_in_model: string; effort: string;
  theme: string; morning_at: string; evening_at: string; weekly_at: string;
}

export interface Value { key: string; name: string; colour: string; sort: number }
export interface Area { id: string; name: string; note: string | null; values: { value_id: string; role: string; why: string | null }[] }

export interface Board {
  today: string; values: Value[]; areas: Area[]; tasks: Task[];
  flags: Flag[]; rhythms: Rhythm[]; collections: Collection[];
  travel: Travel[]; routines: Routines; slots: Slot[];
}

export interface Today {
  date: string;
  /** Set while today falls inside a trip. The brief stops asking, and says why. */
  travel: Travel | null;
  heavy: (Task & { why_today: string | null }) | null;
  then: Task[];
  quiet_hour: { task: Task; value: string; last_touched: string | null } | null;
  flag: Flag | null;
  /** Null inside a travel range: one of the things the brief stops doing. */
  question: string | null;
  rhythms: string[];
  slots: Slot[];
  routines: Routines;
  office_open: boolean;
}

export interface Horizon {
  from: string; to: string;
  dated: Task[];
  lead_time: (Task & { starts_now_for: { id: string; title: string; due: string | null }[] })[];
  flags: Flag[];
  travel: Travel[];
}

/** A recorded morning/evening/weekly sitting. `wrote` is whatever `recordSession` was given. */
export interface Session { id: string; kind: "morning" | "evening" | "weekly"; date: string; wrote: unknown[] }

/**
 * What `/api/search` returns. A task hit is the full `Task` the board already
 * serves, so the existing row and detail sheet take it as it is.
 */
export type SearchHit =
  | (Task & { kind: "task" })
  | { kind: "flag"; id: string; title: string; body: string | null; state: "open" | "resolved";
      resolution: string | null; tasks: string[] }
  | { kind: "item"; id: string; text: string; collection_id: string; collection_name: string;
      value_id: string; value_name: string; archived: boolean; promoted_task_id: string | null };

export interface SearchFilters { value: string[]; tier: string[]; state: string[] }

export class Unauthorised extends Error {}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (res.status === 401) throw new Unauthorised("unauthorised");
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The status rides along so a caller can tell "there is no such document"
    // from "the request failed". A prompt nobody has written yet is a normal
    // state of a session, not an error to paint red.
    throw Object.assign(new Error((body as any).error ?? `request failed`), { status: res.status });
  }
  return body as T;
}

export const api = {
  me: () => call<{ authenticated: boolean }>("/auth/me"),
  login: (passphrase: string) => call<{ ok: true }>("/auth/login", { method: "POST", body: JSON.stringify({ passphrase }) }),
  logout: () => call<{ ok: true }>("/auth/logout", { method: "POST" }),

  board: () => call<Board>("/board"),
  search: (q: string, f: SearchFilters, signal?: AbortSignal) => {
    const qs = new URLSearchParams({ q });
    if (f.value.length) qs.set("value", f.value.join(","));
    if (f.tier.length) qs.set("tier", f.tier.join(","));
    if (f.state.length) qs.set("state", f.state.join(","));
    return call<SearchHit[]>(`/search?${qs}`, { signal });
  },
  today: () => call<Today>("/today"),
  task: (id: string) => call<Task & { blockers: Task[]; unblocking: Task[]; history: any[]; flags: Flag[] }>(`/tasks/${id}`),

  create: (patch: Record<string, unknown>) =>
    call<{ id: string; created: true; why_prompt: string | null }>("/tasks", { method: "POST", body: JSON.stringify(patch) }),

  update: (id: string, patch: Record<string, unknown>) =>
    call<{ id: string; changes: { field: string; from: unknown; to: unknown }[] }>(`/tasks/${id}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  // Nothing is deleted. This is the nearest thing and it needs a reason.
  park: (id: string, because: string, review_when?: string) =>
    call(`/tasks/${id}/park`, { method: "POST", body: JSON.stringify({ because, review_when }) }),

  addCollectionItem: (collectionId: string, text: string) =>
    call<CollectionItem>(`/collections/${collectionId}/items`, { method: "POST", body: JSON.stringify({ text }) }),

  promote: (itemId: string, task: Record<string, unknown>) =>
    call<{ id: string }>(`/collection-items/${itemId}/promote`, { method: "POST", body: JSON.stringify(task) }),

  log: (entry: { kind: string; text?: string; task_id?: string }) =>
    call(`/log`, { method: "POST", body: JSON.stringify(entry) }),

  money: (days = 90) => call<MoneyPanel>(`/money?days=${days}`),
  calendar: (from: string, to: string) => call<CalendarView>(`/calendar?from=${from}&to=${to}`),
  staleness: () => call<Staleness>("/calendar/staleness"),
  pollCalendar: () => call(`/calendar/poll`, { method: "POST" }),

  // The whole of the interaction with a slot. There is no client method that
  // reads a slot's history, because there is no endpoint that returns one.
  advanceSlot: (id: string) =>
    call<{ id: string; name: string; done: string; next: string }>(`/slots/${id}/advance`, { method: "POST" }),

  // The assistant. Every model call happens on the server: the key is in the
  // api container's environment and never reaches this bundle, which is
  // static files served by nginx and would be a leak.
  assistantOptions: () => call<AssistantOptions>("/assistant/models"),
  assistantChat: (body: { messages: unknown[]; model?: string; effort?: string }) =>
    call<AssistantReply>("/assistant/chat", { method: "POST", body: JSON.stringify(body) }),

  // Yes, no, and put it back. There is no client method that writes without a
  // proposal id, because there is no such route — the id is what makes the
  // change that runs the change he was shown.
  assistantConfirm: (body: { proposal_id: string; messages: unknown[]; model?: string }) =>
    call<AssistantReply>("/assistant/confirm", { method: "POST", body: JSON.stringify(body) }),
  assistantDecline: (body: { proposal_id: string; messages: unknown[]; model?: string }) =>
    call<AssistantReply>("/assistant/decline", { method: "POST", body: JSON.stringify(body) }),
  // No model call behind this one: it runs the inverse and returns. It costs
  // nothing and keeps working when the key does not.
  assistantUndo: (batch_id: string) =>
    call<{ undone: string[] }>("/assistant/undo", { method: "POST", body: JSON.stringify({ batch_id }) }),

  assistantCost: () => call<AssistantCost>("/assistant/cost"),

  settings: () => call<Settings>("/settings"),
  updateSettings: (patch: Partial<Settings>) =>
    call<Settings>("/settings", { method: "PATCH", body: JSON.stringify(patch) }),

  // The pantry. Read the list, tick one item. There is no client method that
  // reads a restocking history or a count of what is missing, because there is
  // no endpoint that returns one.
  pantry: () => call<Pantry>("/pantry"),
  setPantryItem: (id: string, in_stock: boolean) =>
    call<PantryItem>(`/pantry/${encodeURIComponent(id)}`, {
      method: "PATCH", body: JSON.stringify({ in_stock }),
    }),

  routines: () => call<Routines>("/routines"),
  addRoutine: (slot: string, name: string) =>
    call<Routine>("/routines", { method: "POST", body: JSON.stringify({ slot, name }) }),
  renameRoutine: (id: string, name: string) =>
    call<Routine>(`/routines/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  parkRoutine: (id: string, because: string) =>
    call(`/routines/${id}/park`, { method: "POST", body: JSON.stringify({ because }) }),

  // Read-only, and deliberately so: there is no write here and the Docs view
  // offers no way to make one. Documents are edited from a chat, through the
  // connector's write_doc / edit_doc, where a change gets a git commit naming
  // the tool. A textarea on a phone would be a second, unattributed way in.
  docs: () => call<{ docs: Doc[] }>("/docs"),
  doc: (path: string) =>
    call<{ path: string; content: string }>(`/docs/${path.split("/").map(encodeURIComponent).join("/")}`),

  travel: () => call<Travel[]>("/travel"),
  addTravel: (t: { starts_on: string; ends_on: string; label: string; note?: string }) =>
    call<Travel>("/travel", { method: "POST", body: JSON.stringify(t) }),

  horizon: (days = 21) => call<Horizon>(`/horizon?days=${days}`),
  flags: () => call<Flag[]>("/flags"),
  resolveFlag: (id: string, resolution: string) =>
    call<{ id: string; title: string; state: string; resolution: string }>(`/flags/${id}/resolve`, {
      method: "POST", body: JSON.stringify({ resolution }),
    }),

  // Sessions: the record a morning/evening/weekly sitting leaves. `recordSession`
  // is the same route the assistant's own evening/weekly framing writes through
  // in front of — this is not a new backend contract, just the client half of
  // one that already existed for the MCP tools.
  sessions: (limit = 30) => call<Session[]>(`/sessions?limit=${limit}`),
  recordSession: (body: { kind: "morning" | "evening" | "weekly"; transcript?: string | null; wrote?: unknown[]; date?: string }) =>
    call<Session>("/sessions", { method: "POST", body: JSON.stringify(body) }),
};
